import type {
  ChoghadiyaPeriod,
  ChoghadiyaResponse,
  ChoghadiyaTimePeriod,
  ChoghadiyaVara,
  PanchangPlanet,
  PanchangResponse,
  PersonalPanchangResponse as ApiPersonalPanchangResponse,
} from "@ishvaram/api-client";

import { edgeRead } from "./_fetch";

export type {
  ChoghadiyaPeriod,
  ChoghadiyaResponse,
  ChoghadiyaTimePeriod,
  ChoghadiyaVara,
  PanchangPlanet,
  PanchangResponse,
};

export const IST_TZ_OFFSET_HOURS = 5.5;

export async function getPanchang(params: {
  date?: string;
  latitude?: number;
  longitude?: number;
  /** IANA timezone. Preferred for DST-aware local engine times. */
  tz?: string;
  /** Offset from UTC in hours. India is 5.5, not 330 minutes. */
  tz_offset?: number;
}): Promise<PanchangResponse> {
  const q = new URLSearchParams();
  if (params.date) q.set("date", params.date);
  if (params.latitude !== undefined) q.set("latitude", String(params.latitude));
  if (params.longitude !== undefined) q.set("longitude", String(params.longitude));
  if (params.tz) q.set("tz", params.tz);
  if (params.tz_offset !== undefined) q.set("tz_offset", String(params.tz_offset));
  return edgeRead<PanchangResponse>("panchang", q.toString());
}

export async function fetchChoghadiya(params: {
  date?: string;
  latitude?: number;
  longitude?: number;
  /** Offset from UTC in hours. India is 5.5, not 330 minutes. */
  tz_offset?: number;
}): Promise<ChoghadiyaResponse> {
  const q = new URLSearchParams();
  if (params.date) q.set("date", params.date);
  if (params.latitude !== undefined) q.set("latitude", String(params.latitude));
  if (params.longitude !== undefined) q.set("longitude", String(params.longitude));
  if (params.tz_offset !== undefined) q.set("tz_offset", String(params.tz_offset));
  return edgeRead<ChoghadiyaResponse>("choghadiya", q.toString());
}

/** One Choghadiya window. `quality` is best | good | neutral | bad (engine vocab). */
export interface ChoghadiyaPeriodLite {
  name: string;
  name_hi?: string;
  quality: "best" | "good" | "neutral" | "bad" | string;
  start_time: string; // HH:MM in the response's tz_offset — display only
  end_time: string; // HH:MM in the response's tz_offset — display only
  /**
   * Offset-bearing ISO-8601 instants for the same boundaries. Only these are
   * moments in time: `start_time`/`end_time` carry no date, so a night period
   * that crosses midnight is indistinguishable from an earlier one, and no
   * client can tell which zone they belong to (rule 25). Anything that compares
   * against the current time reads these.
   *
   * Optional because a response served from an edge cache written before this
   * field shipped will not have it.
   */
  starts_at?: string | null;
  ends_at?: string | null;
}

export interface PersonalPanchangWindow {
  start?: string | null;
  end?: string | null;
}

export interface PersonalPanchangTiming {
  sunrise?: string | null;
  sunset?: string | null;
  day_choghadiya: ChoghadiyaPeriodLite[];
  night_choghadiya: ChoghadiyaPeriodLite[];
  rahu_kaal: PersonalPanchangWindow;
  abhijit: PersonalPanchangWindow;
}

/** Generated API response with the OpenAPI-opaque timing payload narrowed for UI use. */
export type PersonalPanchangResponse = Omit<ApiPersonalPanchangResponse, "timing"> & {
  timing?: PersonalPanchangTiming | null;
};

export async function getPersonalPanchang(
  readingId: string,
  forDate?: string
): Promise<PersonalPanchangResponse> {
  const q = new URLSearchParams();
  if (forDate) q.set("for_date", forDate);
  return edgeRead<PersonalPanchangResponse>(
    `panchang/personal/${encodeURIComponent(readingId)}`,
    q.toString()
  );
}
